/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { CreateInvoiceSchema, type CreateInvoiceInput } from '@therapy/shared-validators';
import {
  type ChangeEvent,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { type UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type ServiceItem, type CreateInvoicePayload } from '../../lib/api/finance.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useCreateInvoice, useServiceItems } from '../../lib/queries/finance.js';
import { formatInr, rupeesToPaise } from '../../lib/utils/money.js';

interface InvoiceFormProps {
  readonly onDone: () => void;
}

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** Drop empty optional fields so the API receives only the keys that were set. */
function buildPayload(values: CreateInvoiceInput): CreateInvoicePayload {
  return {
    clientId: values.clientId,
    amountPaise: values.amountPaise,
    ...(values.serviceItemId !== undefined ? { serviceItemId: values.serviceItemId } : {}),
    ...(values.dueDate !== undefined && values.dueDate !== '' ? { dueDate: values.dueDate } : {}),
    ...(values.notes !== undefined && values.notes !== '' ? { notes: values.notes } : {}),
    ...(values.status !== undefined ? { status: values.status } : {}),
  };
}

interface ServiceSelectProps {
  readonly itemId: string;
  readonly services: readonly ServiceItem[];
  readonly onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

function ServiceSelect({ itemId, services, onChange }: ServiceSelectProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor="invoice-service" className={LABEL_CLASS}>
        {t('form.invoice.service')}
      </label>
      <select id="invoice-service" className={SELECT_CLASS} value={itemId} onChange={onChange}>
        <option value="">{t('form.invoice.serviceCustom')}</option>
        {services.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} — {formatInr(s.unitPricePaise)}
          </option>
        ))}
      </select>
    </div>
  );
}

interface AmountFieldProps {
  readonly picked: ServiceItem | undefined;
  readonly register: UseFormRegister<CreateInvoiceInput>;
  readonly error: string | undefined;
}

function AmountField({ picked, register, error }: AmountFieldProps): JSX.Element {
  const { t } = useTranslation();
  if (picked !== undefined) {
    return (
      <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {t('form.invoice.amount')}: <strong>{formatInr(picked.unitPricePaise)}</strong>
      </p>
    );
  }
  return (
    <FormField
      id="invoice-amount"
      type="number"
      label={t('form.invoice.amount')}
      placeholder={t('form.invoice.amountHint')}
      autoComplete="off"
      inputMode="numeric"
      error={error}
      registration={register('amountPaise', {
        setValueAs: (v: string) => rupeesToPaise(Number(v)),
      })}
    />
  );
}

/** "New Invoice" form (Figma Finance). Validated by shared `CreateInvoiceSchema`. */
export function InvoiceForm({ onDone }: InvoiceFormProps): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const services = useServiceItems(true);
  const create = useCreateInvoice();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateInvoiceInput>({ resolver: zodResolver(CreateInvoiceSchema) });
  // A catalogue pick fixes the price; '' keeps the manual amount field.
  const [itemId, setItemId] = useState('');
  const items = useMemo(() => services.data ?? [], [services.data]);

  const onPickService = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const id = event.target.value;
      setItemId(id);
      const item = items.find((s) => s.id === id);
      setValue('serviceItemId', id === '' ? undefined : id);
      if (item !== undefined) {
        // The amount field converts rupees → paise on submit (`setValueAs`), so
        // seed it in RUPEES; writing paise here would be ×100'd again on save.
        setValue('amountPaise', item.unitPricePaise / 100, { shouldValidate: true });
      }
    },
    [items, setValue],
  );

  const submit = useCallback(
    async (values: CreateInvoiceInput): Promise<void> => {
      await create.mutateAsync(buildPayload(values));
      onDone();
    },
    [create, onDone],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <div>
        <label htmlFor="invoice-client" className={LABEL_CLASS}>
          {t('form.invoice.client')}
        </label>
        <select
          id="invoice-client"
          className={SELECT_CLASS}
          defaultValue=""
          aria-invalid={errors.clientId !== undefined}
          {...register('clientId')}
        >
          <option value="" disabled>
            {t('form.invoice.selectClient')}
          </option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.fullName}
            </option>
          ))}
        </select>
        {errors.clientId !== undefined && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.clientId.message}
          </p>
        )}
      </div>

      <ServiceSelect itemId={itemId} services={items} onChange={onPickService} />
      <AmountField
        picked={items.find((s) => s.id === itemId)}
        register={register}
        error={errors.amountPaise?.message}
      />
      <FormField
        id="invoice-due"
        type="date"
        label={t('form.invoice.dueDate')}
        autoComplete="off"
        error={errors.dueDate?.message}
        registration={register('dueDate')}
      />
      <FormField
        id="invoice-notes"
        type="text"
        label={t('form.invoice.notes')}
        placeholder={t('form.invoice.notesHint')}
        autoComplete="off"
        error={errors.notes?.message}
        registration={register('notes')}
      />

      {create.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || create.isPending}>
          {t('form.invoice.submit')}
        </Button>
      </div>
    </form>
  );
}
